import React, { FC, useMemo } from 'react';

import { sumBy } from 'lodash';

import { Select } from 'components/DataEntry';
import { getIcon } from 'components/Icons/Components';
import { SourceGroupLabel } from 'containers/Visibility/SecurityGraphNext/Components/SourceGroupLabel';
import { sourceEntitySelectStyles } from 'containers/Visibility/SecurityGraphNext/utils/sourceEntitySelectStyles';
import {
  EXTERNAL_ACCOUNTS,
  SourceAccountFacet,
  splitAccountFacets,
} from 'containers/Visibility/SecurityGraphNext/utils/sourceOptions';

interface ISourceAccountFilter {
  facets: SourceAccountFacet[];
  // the resource's account on a resource map; splits same from cross-account
  resourceAccountId?: string;
  value: string;
  onChange: (accountKey: string) => void;
}

const toOption = (facet: SourceAccountFacet) => ({
  label: `${facet.label} (${facet.count})`,
  value: facet.key,
  subLabel: facet.key === EXTERNAL_ACCOUNTS ? 'Not onboarded' : facet.number,
  icon: getIcon(facet.cloudType ?? 'Account'),
});

// Picks the account the entity dropdown lists, with the server's counts for
// the whole list. It looks like the entity dropdown: the count in brackets
// after the name, the account number on a second line. On a resource map the
// accounts are grouped as the resource's own and the cross-account ones.
export const SourceAccountFilter: FC<ISourceAccountFilter> = ({
  facets,
  resourceAccountId,
  value,
  onChange,
}) => {
  const { options, flat } = useMemo(() => {
    const all = {
      label: `All accounts (${sumBy(facets, 'count')})`,
      value: '',
      icon: getIcon('Account'),
    };
    const entries = facets.map(toOption);
    if (!resourceAccountId) {
      return { options: [all, ...entries], flat: [all, ...entries] };
    }
    const { same, cross } = splitAccountFacets(facets, resourceAccountId);
    const groups = [
      { label: 'Same account', options: same.map(toOption) },
      { label: 'Cross-account', options: cross.map(toOption) },
    ].filter(group => group.options.length);
    return { options: [all, ...groups], flat: [all, ...entries] };
  }, [facets, resourceAccountId]);

  return (
    <Select
      options={options}
      value={flat.find(option => option.value === value) ?? flat[0]}
      onChange={selected => onChange(selected?.value ?? '')}
      isSearchable={false}
      menuPortalTarget={document.body}
      showIconInValueContainer
      showClearIndicator={false}
      styles={sourceEntitySelectStyles}
      formatGroupLabel={group => <SourceGroupLabel label={group.label} />}
      isAFilter
    />
  );
};
